import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiClient, unwrap } from '@/lib/api-client';
import { firstErrorMessage } from './term-versions';

export const checkInKeys = {
  all: ['check-in'] as const,
  events: () => [...checkInKeys.all, 'events'] as const,
  console: (eventId: string) => [...checkInKeys.all, 'events', eventId] as const,
};

const checkIn = apiClient['check-in'] as any;

/** Operações que o portão pode abrir (`GET /check-in/events`). */
export function useCheckInEvents() {
  return useQuery({
    queryKey: checkInKeys.events(),
    queryFn: async () => {
      const res = await checkIn.events.get();
      return unwrap(res.data) as {
        id: string;
        name: string;
        city: string;
        date: string;
        endDate: string;
        period: string;
        kind: 'forja' | 'kids' | 'outro';
        live: boolean;
        total: number;
        done: number;
      }[];
    },
  });
}

/** Operação + recrutas + próximo número previsto (`GET /check-in/events/:id`). */
export function useCheckInConsole(eventId?: string) {
  return useQuery({
    queryKey: checkInKeys.console(eventId ?? ''),
    queryFn: async () => {
      const res = await checkIn.events({ id: eventId }).get();
      return unwrap(res.data) as {
        event: {
          id: string;
          name: string;
          city: string;
          date: string;
          endDate: string;
          period: string;
          kind: 'forja' | 'kids' | 'outro';
          value: number;
          sequence: 'forja' | 'kids';
        };
        soldiers: {
          id: string;
          name: string;
          age: number | null;
          city: string;
          photo: string | null;
          paid: boolean | null;
          soldierNumber: number | null;
          checkedInAt: string | null;
          paidAtCheckIn: boolean;
        }[];
        nextNumber: number;
      };
    },
    enabled: !!eventId,
  });
}

/**
 * Identifica o recruta (`POST /check-in/soldiers/:id`). Erro da regra ("Recruta já está
 * identificado!") vira toast; em qualquer caso a lista é recarregada.
 */
export function useCheckIn() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (soldierId: string) => {
      const res = await checkIn.soldiers({ id: soldierId }).post();
      if (res.error) throw firstErrorMessage(res.error.value);
      return unwrap(res.data) as {
        id: string;
        soldierNumber: number;
        paidAtCheckIn: boolean;
      };
    },
    onError: (e: any) => {
      toast.error(e.message ?? 'Não foi possível fazer o check-in.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: checkInKeys.all });
    },
  });
}
